import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, DollarSign, Archive } from 'lucide-react';
import { TeamProject } from '@/types/TeamProject';

interface TeamProjectCardProps {
  project: TeamProject;
  onSelect: (project: TeamProject) => void;
  onArchive: (projectId: string) => void;
  hideArchive?: boolean;
}

export function TeamProjectCard({ project, onSelect, onArchive, hideArchive = false }: TeamProjectCardProps) {
  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onSelect(project)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{project.name}</CardTitle>
            {project.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {project.description}
              </p>
            )}
          </div>
          <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
            {project.status}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Project Stats */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>{project.member_count || 0} members</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span>${project.hourly_rate}/hr</span>
            </div>
          </div>

          {/* Client Info */}
          {project.client_name && (
            <div className="text-sm">
              <span className="text-muted-foreground">Client: </span>
              <span className="font-medium">{project.client_name}</span>
            </div>
          )}

          {/* User Role */}
          {project.user_role && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {project.user_role}
              </Badge>
              {!hideArchive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(project.id);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <Archive className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}